import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import FullScreenPanel from "../ui/FullScreenPanel.jsx";
import { phoneUi } from "../ui/phoneUi";
import {
  STATIONS,
  effectiveShift,
  formatDate,
  formatDisplayDate,
  getHoursFromServer,
  hoursForToggle,
  hoursToSave,
  jobScheduleTasks,
  moveTaskInDay,
  parseISODate,
  partTaskMatches,
  shiftConfigFromApi,
  tasksByDate,
} from "../../features/scheduling/scheduleRules";
import {
  clearDayOrder,
  clearSecondShiftOverride,
  clearTaskOverride,
  loadAllStationsTasks,
  loadJobSchedule,
  loadShiftConfig,
  loadStationQueue,
  overrideTaskDate,
  resolveJobInput,
  saveDayOrder,
  saveSecondShift,
  saveSecondShiftDefault,
  secondShiftDefaultQuestion,
  setDayLock,
} from "../../features/scheduling/scheduleData";
import "../mobile.css";

// The phone Scheduling page (plan Phase 2): a station's day with its tasks in order. Move a task up
// or down (the desktop drags), reset to the auto order, change or clear a task's date, lock / unlock
// the day, second shift on / off and its hours. Read views: All stations for a day, a job's tasks, a
// part's tasks. Bulk reschedule, the full auto-schedule and the Recommended tab stay on the desktop.
// Layout only: features/scheduling/ is the desktop page's own code.

const VIEWS = [
  { key: "station", label: "Station" },
  { key: "all", label: "All stations" },
  { key: "job", label: "Job" },
  { key: "part", label: "Part" },
];
const hm = (h) => {
  const mins = Math.round((h || 0) * 60);
  return mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
};
const shiftDay = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

/** "Change date" for one task: a date, then the shared override (its own question + result). */
function DatePanel({ task, onClose, onDone }) {
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDate(task?.scheduled_date ? task.scheduled_date.slice(0, 10) : ""); }, [task]);
  const save = async () => {
    setBusy(true);
    const r = await overrideTaskDate({ taskId: task.task_id, date, ui: phoneUi });
    setBusy(false);
    if (r.done) onDone();
  };
  return (
    <FullScreenPanel
      open={!!task}
      title="Change the task's date"
      onClose={busy ? undefined : onClose}
      testId="sched-date-panel"
      footer={<button type="button" className="btn btn-primary btn-lg w-100" disabled={busy || !date} onClick={save}>{busy ? "Saving…" : "Save the date"}</button>}
    >
      {task && <div className="fw-semibold mb-2">#{task.master_job_num} {task.product_id || task.workstation} <span className="jq-muted">{task.product_description}</span></div>}
      <label className="form-label" htmlFor="sched-date">Scheduled for</label>
      <input id="sched-date" type="date" className="form-control form-control-lg" value={date} onChange={(e) => setDate(e.target.value)} />
      <div className="jq-muted mt-2">A changed date is pinned: the auto-scheduler leaves it until you clear it.</div>
    </FullScreenPanel>
  );
}

function TaskCard({ task, index, count, busy, onMove, onDate, onClear }) {
  const pinned = task.manual_sort_position != null;
  return (
    <div className="jq-card" data-testid={`sched-task-${task.task_id}`}>
      <div className="d-flex align-items-start gap-2">
        <div className="flex-grow-1">
          <div className="fw-semibold">
            {index != null && <span className="jq-muted me-1">{index + 1}.</span>}
            #{task.master_job_num} {task.product_id || task.workstation}
            {pinned && <span className="jq-tag ms-1" title="Pinned in this order">pinned</span>}
            {task.manual_override && <span className="jq-tag jq-tag-warn ms-1">date set by hand</span>}
          </div>
          {task.product_description && <div className="jq-muted">{task.product_description}</div>}
          {!task.product_id && task.contained_parts_str && <div className="jq-muted">Batch: {task.contained_parts_str}</div>}
          <div className="jq-muted">
            Qty {task.quantity ?? "—"} · P{task.priority_level} · {task.task_status} · {hm(getHoursFromServer(task))}
            {task.due_date ? ` · due ${formatDisplayDate(parseISODate(task.due_date))}` : ""}
          </div>
          {task.workstation === "Laser" && task.material_description && (
            <div className="jq-muted">{task.sheet_required || 0} sheet(s) · {task.material_description}</div>
          )}
        </div>
        {onMove && (
          <div className="d-flex flex-column gap-1">
            <button type="button" className="btn btn-outline-secondary btn-sm" aria-label={`Move #${task.master_job_num} ${task.product_id || ""} up`} disabled={busy || index === 0} onClick={() => onMove(task, -1)}>
              <i className="fas fa-arrow-up" aria-hidden="true" />
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" aria-label={`Move #${task.master_job_num} ${task.product_id || ""} down`} disabled={busy || index === count - 1} onClick={() => onMove(task, 1)}>
              <i className="fas fa-arrow-down" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
      <div className="d-flex gap-2 mt-2">
        <button type="button" className="btn btn-outline-primary btn-sm" disabled={busy} onClick={() => onDate(task)}>Change date</button>
        {task.manual_override && <button type="button" className="btn btn-outline-danger btn-sm" disabled={busy} onClick={() => onClear(task)}>Clear date</button>}
        <Link to={`/jobs/${task.job_id}`} className="btn btn-link btn-sm ms-auto">Job</Link>
      </div>
    </div>
  );
}

function StationDay({ station, setStation, day, setDay }) {
  const [tasks, setTasks] = useState(null);
  const [locked, setLocked] = useState(null); // the locked days; null = not read (yet) for this station
  const [shift, setShift] = useState(null); // shiftConfigFromApi; null until /shifts/config is read
  const [shiftFailed, setShiftFailed] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hours, setHours] = useState("");
  const [dateTask, setDateTask] = useState(null);
  const [showUnscheduled, setShowUnscheduled] = useState(false);
  const seq = useRef(0);
  const stationRef = useRef(station);
  stationRef.current = station;

  // Reads the station ON SCREEN: an action's re-read that finishes after a station change loads the
  // new station, never the one the action started on. The newest read wins.
  const loadQueue = useCallback(async () => {
    const st = stationRef.current;
    const mine = ++seq.current;
    try {
      const r = await loadStationQueue(st);
      if (mine !== seq.current) return;
      if (r.failed) { setError(`Couldn't load the ${st} queue.`); setTasks([]); return; }
      setTasks(r.tasks);
      if (r.lockedDates) setLocked(r.lockedDates);
      setError(null);
    } catch (err) {
      if (mine === seq.current) setError(err.message || "Couldn't reach JobQ.");
    }
  }, []);

  const loadShift = useCallback(async () => {
    try {
      const data = await loadShiftConfig();
      if (data) setShift(shiftConfigFromApi(data));
      setShiftFailed(!data);
    } catch (_) {
      setShiftFailed(true);
    }
  }, []);

  useEffect(() => { setTasks(null); setLocked(null); loadQueue(); }, [station, loadQueue]);
  useEffect(() => { loadShift(); }, [loadShift]);
  useEffect(() => () => { seq.current += 1; }, []);

  const dayKey = formatDate(day);
  const byDate = useMemo(() => tasksByDate(tasks || []), [tasks]);
  const dayTasks = (byDate[dayKey] || []).filter((t) => t.workstation !== "Shipping");
  const unscheduled = byDate.Unscheduled || [];
  // The second-shift controls work only on settings read from the API: "off" before the read would
  // make "off" the weekday default, or write 4 h over a 6 h default.
  const shiftReady = !!shift && !shiftFailed;
  const eff = shift ? effectiveShift(shift.defaults, shift.overrides, station, day) : { enabled: false, hours: 0, source: "off" };
  const hasOverride = !!shift?.overrides[`${station}|${dayKey}`];
  const capacity = (shift?.firstShiftHours ?? 7) + (eff.enabled ? eff.hours : 0);
  const load = dayTasks.reduce((n, t) => n + getHoursFromServer(t), 0);
  const isLocked = !!locked?.has(dayKey);
  useEffect(() => { setHours(String(eff.hours ?? 0)); }, [eff.hours, station, dayKey]);

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  const move = (task, delta) => run(async () => {
    const order = moveTaskInDay(dayTasks.map((t) => t.task_id), task.task_id, delta);
    if (!order) return;
    await saveDayOrder({ station, date: dayKey, taskIds: order, ui: phoneUi });
    await loadQueue();
  });

  const resetOrder = () => run(async () => {
    const r = await clearDayOrder({ station, dateObj: day, date: dayKey, ui: phoneUi });
    if (r.done) await loadQueue();
  });

  const toggleLock = () => run(async () => {
    await setDayLock({ station, date: dayKey, locked: !isLocked, ui: phoneUi, afterSave: () => loadQueue() });
  });

  const setShiftTo = (enabled, hrs) => run(async () => {
    const r = await saveSecondShift({ station, date: dayKey, enabled, hours: hrs, ui: phoneUi });
    if (r.done) await loadShift();
    else setHours(String(eff.hours ?? 0)); // refused: the box shows the day's hours again
  });

  const commitHours = () => {
    const hrs = hoursToSave(hours, eff);
    if (hrs == null) { setHours(String(eff.hours ?? 0)); return; }
    setShiftTo(eff.enabled, hrs);
  };

  const saveDefault = () => run(async () => {
    if (!(await phoneUi.confirm(secondShiftDefaultQuestion(station, eff), { title: "Second shift default", confirmLabel: "Make it the default" }))) return;
    const r = await saveSecondShiftDefault({ station, date: dayKey, effective: eff, ui: phoneUi });
    if (r.done) await loadShift();
  });

  const clearShiftOverride = () => run(async () => {
    const r = await clearSecondShiftOverride({ station, date: dayKey, ui: phoneUi });
    if (r.done) await loadShift();
  });

  const clearDate = (task) => run(async () => {
    const r = await clearTaskOverride({ taskId: task.task_id, ui: phoneUi });
    if (r.done) await loadQueue();
  });

  return (
    <>
      <div className="d-flex gap-2 mb-2">
        <select className="form-select" aria-label="Station" value={station} onChange={(e) => setStation(e.target.value)}>
          {STATIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="d-flex align-items-center gap-2 mb-2">
        <button type="button" className="btn btn-outline-secondary" aria-label="Previous day" onClick={() => setDay(shiftDay(day, -1))}><i className="fas fa-chevron-left" aria-hidden="true" /></button>
        <input type="date" className="form-control" aria-label="Day" value={dayKey} onChange={(e) => e.target.value && setDay(parseISODate(e.target.value))} />
        <button type="button" className="btn btn-outline-secondary" aria-label="Next day" onClick={() => setDay(shiftDay(day, 1))}><i className="fas fa-chevron-right" aria-hidden="true" /></button>
      </div>

      {error && <div className="jq-note jq-note-danger mb-2" role="alert">{error}</div>}

      <section className="jq-card" data-testid="sched-day">
        <div className="d-flex align-items-center gap-2">
          <span className="fw-bold flex-grow-1">{formatDisplayDate(day)}</span>
          <span className={load > capacity ? "text-danger fw-bold" : "jq-muted"}>{hm(load)} of {hm(capacity)}</span>
        </div>
        <label className="form-check form-switch mt-2">
          <input className="form-check-input" type="checkbox" checked={isLocked} disabled={busy || tasks === null || locked === null} onChange={toggleLock} />
          <span className="form-check-label">
            {locked === null && tasks !== null ? "Day lock couldn't be read" : isLocked ? "Day locked (the auto-scheduler leaves it alone)" : "Day unlocked"}
          </span>
        </label>
        <label className="form-check form-switch mt-1">
          <input className="form-check-input" type="checkbox" checked={!!eff.enabled} disabled={busy || !shiftReady} onChange={(e) => setShiftTo(e.target.checked, hoursForToggle(eff, e.target.checked))} />
          <span className="form-check-label">Second shift{eff.enabled ? ` (${eff.hours} h)` : ""}{eff.source === "default" ? " · default" : eff.source === "override" ? " · this day" : ""}</span>
        </label>
        {eff.enabled && (
          <div className="d-flex align-items-center gap-2 mt-1">
            <label htmlFor="sched-shift-hours" className="jq-muted">Hours</label>
            <input id="sched-shift-hours" type="number" inputMode="decimal" min="0" max="12" step="0.5" className="form-control" style={{ maxWidth: 90 }} value={hours} disabled={busy || !shiftReady} onChange={(e) => setHours(e.target.value)} onBlur={commitHours} />
          </div>
        )}
        {shiftFailed && (
          <div className="jq-note jq-note-danger mt-2" role="alert">
            Couldn't read the second-shift settings, so they can't be changed here right now.{" "}
            <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={loadShift}>Try again</button>
          </div>
        )}
        <div className="d-flex flex-wrap gap-2 mt-2">
          <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy || !shiftReady} onClick={saveDefault}>Make it the weekday default</button>
          {hasOverride && <button type="button" className="btn btn-link btn-sm" disabled={busy || !shiftReady} onClick={clearShiftOverride}>Clear this day's shift</button>}
        </div>
      </section>

      {tasks === null && <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Loading…</div>}
      {tasks !== null && dayTasks.length === 0 && <div className="jq-list-state">Nothing scheduled at {station} on this day.</div>}
      {dayTasks.some((t) => t.manual_sort_position != null) && (
        <button type="button" className="btn btn-outline-secondary w-100 mb-2" disabled={busy} onClick={resetOrder}>Reset to auto order</button>
      )}
      {dayTasks.map((t, i) => (
        <TaskCard key={t.task_id} task={t} index={i} count={dayTasks.length} busy={busy} onMove={move} onDate={setDateTask} onClear={clearDate} />
      ))}
      {unscheduled.length > 0 && (
        <>
          <button type="button" className="btn btn-link px-0" onClick={() => setShowUnscheduled((v) => !v)}>
            {showUnscheduled ? "Hide" : "Show"} {unscheduled.length} unscheduled task(s)
          </button>
          {showUnscheduled && unscheduled.map((t) => <TaskCard key={t.task_id} task={t} busy={busy} onDate={setDateTask} onClear={clearDate} />)}
        </>
      )}

      <DatePanel task={dateTask} onClose={() => setDateTask(null)} onDone={() => { setDateTask(null); loadQueue(); }} />
    </>
  );
}

function AllStations({ day, setDay }) {
  const [tasks, setTasks] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    let live = true;
    loadAllStationsTasks(STATIONS)
      .then((t) => { if (live) setTasks(t); })
      .catch((err) => { if (live) setError(err.message || "Couldn't load the stations."); });
    return () => { live = false; };
  }, []);
  const dayKey = formatDate(day);
  const dayTasks = (tasks || []).filter((t) => (t.scheduled_date || "").slice(0, 10) === dayKey);
  return (
    <>
      <div className="d-flex align-items-center gap-2 mb-2">
        <button type="button" className="btn btn-outline-secondary" aria-label="Previous day" onClick={() => setDay(shiftDay(day, -1))}><i className="fas fa-chevron-left" aria-hidden="true" /></button>
        <input type="date" className="form-control" aria-label="Day" value={dayKey} onChange={(e) => e.target.value && setDay(parseISODate(e.target.value))} />
        <button type="button" className="btn btn-outline-secondary" aria-label="Next day" onClick={() => setDay(shiftDay(day, 1))}><i className="fas fa-chevron-right" aria-hidden="true" /></button>
      </div>
      {error && <div className="jq-note jq-note-danger" role="alert">{error}</div>}
      {!tasks && !error && <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Loading every station…</div>}
      {tasks && STATIONS.map((st) => {
        const list = dayTasks.filter((t) => t.station === st);
        const hrs = list.reduce((n, t) => n + getHoursFromServer(t), 0);
        return (
          <section key={st} className="jq-card" data-testid={`sched-all-${st}`}>
            <button type="button" className="btn btn-link text-reset text-decoration-none w-100 text-start p-0 d-flex" onClick={() => setOpen(open === st ? null : st)}>
              <span className="fw-bold flex-grow-1">{st}</span>
              <span className="jq-muted">{list.length} task(s) · {hm(hrs)}</span>
            </button>
            {open === st && list.map((t) => (
              <div key={t.task_id} className="jq-task-line">
                <span className="jq-task-line-main">#{t.master_job_num} {t.product_id || t.workstation} <span className="jq-muted">{t.task_status} · {hm(getHoursFromServer(t))}</span></span>
              </div>
            ))}
          </section>
        );
      })}
    </>
  );
}

function JobLookup() {
  const [input, setInput] = useState("");
  const [job, setJob] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const [dateTask, setDateTask] = useState(null);
  const lastId = useRef(null);
  const load = async (jobId) => {
    setBusy(true);
    try { setJob(await loadJobSchedule(jobId)); } catch (_) { setJob(null); } finally { setBusy(false); }
  };
  const lookUp = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setJob(undefined);
    setBusy(true);
    const jobId = await resolveJobInput(input.trim());
    lastId.current = jobId;
    await load(jobId);
  };
  const clear = async (task) => {
    const r = await clearTaskOverride({ taskId: task.task_id, ui: phoneUi });
    if (r.done) await load(lastId.current);
  };
  return (
    <>
      <form className="d-flex gap-2 mb-2" onSubmit={lookUp}>
        <input className="form-control" inputMode="numeric" placeholder="Master job #" aria-label="Master job #" value={input} onChange={(e) => setInput(e.target.value)} />
        <button type="submit" className="btn btn-primary" disabled={busy}>Look up</button>
      </form>
      {busy && <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /></div>}
      {job === null && !busy && <div className="jq-note jq-note-danger" role="alert">That job couldn't be read.</div>}
      {job && (
        <div data-testid="sched-job">
          <div className="fw-semibold mb-2">Job #{job.master_job_num ?? job.job_id} · P{job.priority_level} · {job.status}</div>
          {jobScheduleTasks(job.tasks).map((t) => (
            <div key={t.task_id} className="jq-card">
              <div className="fw-semibold">{t.workstation} <span className="jq-muted">{t.product_id || ""}</span></div>
              <div className="jq-muted">{t.task_status || t.status} · {t.scheduled_date ? formatDisplayDate(parseISODate(t.scheduled_date)) : "Unscheduled"}</div>
              <div className="d-flex gap-2 mt-2">
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setDateTask({ ...t, master_job_num: job.master_job_num })}>Change date</button>
                {t.manual_override && <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => clear(t)}>Clear date</button>}
              </div>
            </div>
          ))}
        </div>
      )}
      <DatePanel task={dateTask} onClose={() => setDateTask(null)} onDone={() => { setDateTask(null); load(lastId.current); }} />
    </>
  );
}

function PartLookup() {
  const [input, setInput] = useState("");
  const [all, setAll] = useState(null);
  const [found, setFound] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [dateTask, setDateTask] = useState(null);
  const find = async (fresh) => {
    setBusy(true);
    try {
      const pool = (!fresh && all) || await loadAllStationsTasks(STATIONS);
      setAll(pool);
      setFound(partTaskMatches(pool, input.trim()));
      setError(null);
    } catch (err) {
      setError(err.message || "Couldn't read the stations.");
    } finally {
      setBusy(false);
    }
  };
  const search = async (e) => {
    if (e) e.preventDefault();
    if (!input.trim()) return;
    await find(false);
  };
  const refresh = () => find(true);
  const clear = async (task) => {
    const r = await clearTaskOverride({ taskId: task.task_id, ui: phoneUi });
    if (r.done) await refresh();
  };
  return (
    <>
      <form className="d-flex gap-2 mb-2" onSubmit={search}>
        <input className="form-control" placeholder="Part number or description" aria-label="Part number or description" value={input} onChange={(e) => setInput(e.target.value)} />
        <button type="submit" className="btn btn-primary" disabled={busy}>Find</button>
      </form>
      {busy && <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Reading every station…</div>}
      {error && !busy && <div className="jq-note jq-note-danger" role="alert">{error}</div>}
      {found && !busy && <div className="jq-muted mb-2" data-testid="sched-part-count">{found.length} open task(s)</div>}
      {(found || []).map((t) => (
        <div key={`${t.station}-${t.task_id}`} className="jq-card">
          <div className="fw-semibold">{t.station} · #{t.master_job_num} {t.product_id || ""}</div>
          <div className="jq-muted">{t.task_status} · {t.scheduled_date ? formatDisplayDate(parseISODate(t.scheduled_date)) : "Unscheduled"}</div>
          <div className="d-flex gap-2 mt-2">
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setDateTask(t)}>Change date</button>
            {t.manual_override && <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => clear(t)}>Clear date</button>}
          </div>
        </div>
      ))}
      <DatePanel task={dateTask} onClose={() => setDateTask(null)} onDone={() => { setDateTask(null); refresh(); }} />
    </>
  );
}

export default function SchedulingScreen() {
  const [view, setView] = useState("station");
  const [station, setStation] = useState("Laser");
  const [day, setDay] = useState(() => new Date());
  return (
    <div className="jq-screen" data-testid="phone-scheduling">
      <div className="d-flex flex-wrap gap-2 mb-2" role="group" aria-label="View">
        {VIEWS.map((v) => (
          <button key={v.key} type="button" className={`btn btn-sm ${view === v.key ? "btn-secondary" : "btn-outline-secondary"}`} aria-pressed={view === v.key} onClick={() => setView(v.key)}>
            {v.label}
          </button>
        ))}
      </div>
      {view === "station" && <StationDay station={station} setStation={setStation} day={day} setDay={setDay} />}
      {view === "all" && <AllStations day={day} setDay={setDay} />}
      {view === "job" && <JobLookup />}
      {view === "part" && <PartLookup />}
    </div>
  );
}
